// ========================================
// pages/AddAsset.js - Add New Asset to Vault
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const AddAsset = () => {
  const navigate = useNavigate();
  
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userPlan = user?.plan || 'free';
  
  const assetTypes = [
  { value: 'bank_account', label: '🏦 Bank Account', icon: '🏦' },
  { value: 'real_estate', label: '🏠 Real Estate', icon: '🏠' },
  { value: 'vehicle', label: '🚗 Vehicle', icon: '🚗' },
  { value: 'will', label: '📜 Last Will & Testament', icon: '📜' },
  { value: 'license_code', label: '📜 License/Code', icon: '📜' },
  { value: 'secure_note', label: '🔒 Secure Note', icon: '🔒' },
  { value: 'login_credential', label: '🔐 Login Credential', icon: '🔐' },
  { value: 'custom_asset', label: '📋 Custom Asset', icon: '📋' },
  ];

  const [globalNoks, setGlobalNoks] = useState([]);
  const [externalBeneficiaries, setExternalBeneficiaries] = useState([]);
  const [loadingNoks, setLoadingNoks] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState(1);
  const [assetType, setAssetType] = useState('');
  const [formData, setFormData] = useState({
    assetName: '', institutionName: '', accountNumber: '', routingNumber: '', branch: '', balance: '',
    propertyType: '', address: '', titleDeedNumber: '', estimatedValue: '',
    make: '', model: '', year: '', vin: '',
    dateSigned: '', witnesses: '', executorName: '', specialInstructions: '',
    description: '', additionalInfo: '',
	location: { 
  fullAddress: '', 
  state: '', 
  country: '', 
  countryOther: '',
  gpsCoordinates: '' 
},
    notes: '',
	currency: 'GHS',
	category: 'other',
	secureNoteTitle: '',
    secureNoteContent: '',
	customFields: [],
    newFieldName: '',
    newFieldValue: '',
	platformName: '',
    username: '',
    password: '',
    loginUrl: '',
    beneficiaryType: 'default',
    selectedGlobalNoks: [],
    selectedExternalBeneficiaries: [],
    newExternalBeneficiary: { name: '', email: '', phone: '', relationship: 'other' },
    document: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewExternalForm, setShowNewExternalForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    fetchGlobalNoks();
    fetchExternalBeneficiaries();
  }, []);

  const fetchGlobalNoks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalNoks(res.data.user.globalNextOfKin || []);
    } catch (err) { console.error(err); } finally { setLoadingNoks(false); }
  };

  const fetchExternalBeneficiaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/beneficiaries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExternalBeneficiaries(res.data);
    } catch (err) { console.error(err); }
  };

  const handleTypeSelect = (type) => {
    setAssetType(type);
    setStep(2);
  };

const handleChange = (e) => {
  const { name, value } = e.target;
  
  if (name.startsWith('location.')) {
    const field = name.split('.')[1];
    setFormData({ ...formData, location: { ...formData.location, [field]: value } });
    
    // If country is not "Other", clear countryOther
    if (field === 'country' && value !== 'Other') {
      setFormData(prev => ({
        ...prev,
        location: { ...prev.location, countryOther: '' }
      }));
    }
  } else if (name === 'location.countryOther') {
    setFormData({ 
      ...formData, 
      location: { ...formData.location, countryOther: value, country: value }
    });
	} else if (name === 'currency' && value !== 'Other') {
    setFormData({ ...formData, currency: value, currencyOther: '' });
  } else if (name === 'currencyOther') {
    setFormData({ ...formData, currency: value, currencyOther: value });
  } else if (name.startsWith('newExternal.')) {
    const field = name.split('.')[1];
    setFormData({ ...formData, newExternalBeneficiary: { ...formData.newExternalBeneficiary, [field]: value } });
  } else {
    setFormData({ ...formData, [name]: value });
  }
};
  
  // Helper function to get currency symbol
const getCurrencySymbol = (currencyCode) => {
  const symbols = {
    GHS: '₵',
    USD: '$',
    GBP: '£',
    EUR: '€',
    NGN: '₦',
    ZAR: 'R',
    CAD: 'CA$',
    AUD: 'A$'
  };
  return symbols[currencyCode] || currencyCode;
};
  
  // Add a new custom field
const addCustomField = () => {
  if (!formData.newFieldName.trim()) {
    setError('Field name is required');
    return;
  }
  if (!formData.newFieldValue.trim()) {
    setError('Field value is required');
    return;
  }
  
  setFormData({
    ...formData,
    customFields: [
      ...formData.customFields,
      { fieldName: formData.newFieldName.trim(), fieldValue: formData.newFieldValue.trim() }
    ],
    newFieldName: '',
    newFieldValue: ''
  });
  setError('');
};

// Remove a custom field
const removeCustomField = (index) => {
  const updatedFields = [...formData.customFields];
  updatedFields.splice(index, 1);
  setFormData({ ...formData, customFields: updatedFields });
};

// Update a custom field
const updateCustomField = (index, field, value) => {
  const updatedFields = [...formData.customFields];
  updatedFields[index][field] = value;
  setFormData({ ...formData, customFields: updatedFields });
};

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setFileName(file.name); setError(''); }
  };

  const uploadDocument = async (assetId) => {
    if (!selectedFile) return;
    const fd = new FormData();
    fd.append('document', selectedFile);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/assets/${assetId}/document`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
    } catch (err) { setError(err.response?.data?.error || 'Failed to upload document'); }
  };

  const handleBeneficiaryTypeChange = (type) => {
    setFormData({ ...formData, beneficiaryType: type, selectedGlobalNoks: [], selectedExternalBeneficiaries: [] });
  };

  const handleGlobalNokToggle = (id) => {
    const current = [...formData.selectedGlobalNoks];
    if (current.includes(id)) setFormData({ ...formData, selectedGlobalNoks: current.filter(i => i !== id) });
    else setFormData({ ...formData, selectedGlobalNoks: [...current, id] });
  };

  const handleExternalToggle = (id) => {
    const current = [...formData.selectedExternalBeneficiaries];
    if (current.includes(id)) setFormData({ ...formData, selectedExternalBeneficiaries: current.filter(i => i !== id) });
    else setFormData({ ...formData, selectedExternalBeneficiaries: [...current, id] });
  };

  const handleCreateExternal = async () => {
    const { name, email, phone, relationship } = formData.newExternalBeneficiary;
    if (!name || !email) { alert('Name and email required'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/beneficiaries`, { name, email, phone, relationship }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExternalBeneficiaries([...externalBeneficiaries, res.data.beneficiary]);
      setFormData({ ...formData, newExternalBeneficiary: { name: '', email: '', phone: '', relationship: 'other' } });
      setShowNewExternalForm(false);
      handleExternalToggle(res.data.beneficiary.id);
    } catch (err) { alert(err.response?.data?.error || 'Failed to create beneficiary'); }
  };

// ========================================
// VALIDATE EVERYTHING BEFORE ASSET CREATION
// ========================================
const validateBeforeSubmit = () => {
	
	// 1. Check if asset name is provided
if (!formData.assetName || formData.assetName.trim() === '') {
  setError('Asset name is required');
  return false;
}
	
  // 1. Check if asset type is selected
  if (!assetType) {
    setError('Please select an asset type');
    return false;
  }
  
  // 2. Validate required fields based on asset type
  if (!formData.assetName && assetType !== 'will') {
    setError('Asset name is required');
    return false;
  }
  
  // License/Code validations
if (assetType === 'license_code') {
  if (!formData.productName || formData.productName.trim() === '') {
    setError('Product name is required');
    return false;
  }
  if (!formData.licenseKey || formData.licenseKey.trim() === '') {
    setError('License key is required');
    return false;
  }
}
  
  // Secure Note validations
if (assetType === 'secure_note') {
  if (!formData.secureNoteTitle || formData.secureNoteTitle.trim() === '') {
    setError('Note title is required');
    return false;
  }
  if (!formData.secureNoteContent || formData.secureNoteContent.trim() === '') {
    setError('Note content is required');
    return false;
  }
}
  
  // Login Credential validations
if (assetType === 'login_credential') {
  if (!formData.platformName || formData.platformName.trim() === '') {
    setError('Platform name is required');
    return false;
  }
  if (!formData.username || formData.username.trim() === '') {
    setError('Username is required');
    return false;
  }
  if (!formData.password || formData.password.trim() === '') {
    setError('Password is required');
    return false;
  }
}
  
  // Bank Account validations
  if (assetType === 'bank_account') {
    if (!formData.institutionName) {
      setError('Institution name is required');
      return false;
    }
    if (!formData.accountNumber) {
      setError('Account number is required');
      return false;
    }
    if (!formData.balance) {
      setError('Current balance is required');
      return false;
    }
  }
  
  // Real Estate validations
  if (assetType === 'real_estate') {
    if (!formData.propertyType) {
      setError('Property type is required');
      return false;
    }
    if (!formData.address) {
      setError('Property address is required');
      return false;
    }
    if (!formData.titleDeedNumber) {
      setError('Title deed number is required');
      return false;
    }
    if (!formData.estimatedValue) {
      setError('Estimated value is required');
      return false;
    }
  }
  
  // Vehicle validations
  if (assetType === 'vehicle') {
    if (!formData.make) {
      setError('Vehicle make is required');
      return false;
    }
    if (!formData.model) {
      setError('Vehicle model is required');
      return false;
    }
    if (!formData.year) {
      setError('Vehicle year is required');
      return false;
    }
    if (!formData.vin) {
      setError('VIN is required');
      return false;
    }
    if (!formData.estimatedValue) {
      setError('Estimated value is required');
      return false;
    }
  }
  
  // Will validations
  if (assetType === 'will') {
    if (!selectedFile) {
      setError('Please upload your will document');
      return false;
    }
  }
  
  // Custom Document validations
if (assetType === 'custom_document') {
  if (!formData.assetName || formData.assetName.trim() === '') {
    setError('Document title is required');
    return false;
  }
  // Custom fields are optional - no validation needed
}
  
  // Generic description for other asset types
// Excluded types that don't need description
const noDescriptionNeeded = ['bank_account', 'real_estate', 'vehicle', 'will', 'secure_note', 'login_credential', 'license_code', 'custom_asset'];
if (!noDescriptionNeeded.includes(assetType)) {
  if (!formData.description || formData.description.trim() === '') {
    setError('Description is required');
    return false;
  }
}
  
  // 3. Validate file size based on user plan
  if (selectedFile) {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const plan = user?.plan || 'free';
    
    const maxSizes = {
      free: 2,
      premium: 5,
      business: 10,
      enterprise: 20
    };
    const maxSizeMB = maxSizes[plan] || 2;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
if (selectedFile.size > maxSizeBytes) {
  const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
  
  // Truncate long filenames
  let fileName = selectedFile.name;
  if (fileName.length > 25) {
    fileName = fileName.substring(0, 22) + '...';
  }
  
  setError(`"${fileName}" is ${fileSizeMB}MB. Your ${plan} plan allows ${maxSizeMB}MB max. Compress or upgrade.`);
  return false;
}
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
if (!allowedTypes.includes(selectedFile.type)) {
  const fileExt = selectedFile.name.split('.').pop().toUpperCase();
  setError(`The file "${selectedFile.name}" (${fileExt}) has an invalid format. Only PDF, JPG, JPEG, PNG are allowed.`);
  return false;
}
  }
  
  // 4. Validate beneficiary selection
  if (assetType !== 'will') {
    if (formData.beneficiaryType === 'selected_globals' && formData.selectedGlobalNoks.length === 0) {
      setError('Please select at least one Global Next of Kin');
      return false;
    }
    
    if (formData.beneficiaryType === 'external_only' && formData.selectedExternalBeneficiaries.length === 0) {
      setError('Please add at least one external beneficiary');
      return false;
    }
    
    if (formData.beneficiaryType === 'mixed' && formData.selectedGlobalNoks.length === 0 && formData.selectedExternalBeneficiaries.length === 0) {
      setError('Please select at least one Global Next of Kin or external beneficiary');
      return false;
    }
  }
  
  return true;
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');
  
  // Run all validations BEFORE anything else
  if (!validateBeforeSubmit()) {
    return;
  }
  
  setLoading(true);
  setUploading(true);

  try {
    const token = localStorage.getItem('token');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    
    if (!currentUser?.globalNextOfKin || currentUser.globalNextOfKin.length === 0) {
      setError('You must add at least one Global Next of Kin before creating assets.');
      setLoading(false);
      setUploading(false);
      return;
    }

    // Prepare asset data
    const assetData = {
      assetType, ...formData,
      balance: formData.balance ? parseFloat(formData.balance) : undefined,
      estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : undefined,
      year: formData.year ? parseInt(formData.year) : undefined,
      beneficiaryType: formData.beneficiaryType,
      selectedGlobalNoks: formData.selectedGlobalNoks,
      selectedExternalBeneficiaries: formData.selectedExternalBeneficiaries
    };
    
    if (assetType === 'will') {
      assetData.beneficiaryType = 'default';
      assetData.selectedGlobalNoks = [];
      assetData.selectedExternalBeneficiaries = [];
    }

    // Create asset
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/assets`, assetData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const assetId = res.data.asset.id;
    
    // Upload document if exists (validations already passed)
    if (selectedFile) {
      const fd = new FormData();
      fd.append('document', selectedFile);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/assets/${assetId}/document`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
    }

    setSuccess('Asset added successfully!');
    setTimeout(() => {
      setStep(1);
      setAssetType('');
      setFormData({
        assetName: '', institutionName: '', accountNumber: '', routingNumber: '', branch: '', balance: '',
        propertyType: '', address: '', titleDeedNumber: '', estimatedValue: '',
        make: '', model: '', year: '', vin: '',
        dateSigned: '', witnesses: '', executorName: '', specialInstructions: '',
        description: '', additionalInfo: '',
        location: { address: '', region: '', gpsCoordinates: '' },
        notes: '',
		secureNoteTitle: '',
        secureNoteContent: '',
		customFields: [],
        newFieldName: '',
        newFieldValue: '',
		productName: '',
        licenseKey: '',
        expiryDate: '',
        vendor: '',
        beneficiaryType: 'default',
        selectedGlobalNoks: [], selectedExternalBeneficiaries: [],
        newExternalBeneficiary: { name: '', email: '', phone: '', relationship: 'other' },
        document: null
      });
      setSelectedFile(null);
      setFileName('');
      setShowNewExternalForm(false);
      setUploadProgress(0);
      navigate('/dashboard');
    }, 2000);
    
  } catch (err) {
    setError(err.response?.data?.error || 'Failed to add asset');
  } finally {
    setLoading(false);
    setUploading(false);
  }
};

  const renderTypeSelection = () => (
    <div>
      <h2 style={{ fontSize: '20px', color: '#FFD700', marginBottom: '20px' }}>Select Asset Type</h2>
      <div className="asset-grid" style={{ display: 'grid', gap: '20px' }}>
        {assetTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => handleTypeSelect(type.value)}
            style={{ padding: '20px', background: '#F5F0E6', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'all 0.3s' }}
          >
            <span style={{ fontSize: '32px' }}>{type.icon}</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1A1A1A', textAlign: 'center' }}>{type.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderBeneficiarySelection = () => {
    if (assetType === 'will') {
      return (
        <div style={{ background: '#e8f4fd', border: '1px solid #2E7D32', borderRadius: '5px', padding: '15px', marginTop: '20px' }}>
          <p><strong>📜 Will Document:</strong> This will be visible to ALL Global Next of Kin upon death verification.</p>
        </div>
      );
    }

    return (
      <div style={{ marginTop: '30px', padding: '20px', background: '#F5F0E6', borderRadius: '10px' }}>
        <h3 style={{ fontSize: '18px', color: '#2E7D32', marginBottom: '15px' }}>👥 Beneficiary Assignment</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="radio" name="beneficiaryType" value="default" checked={formData.beneficiaryType === 'default'} onChange={() => handleBeneficiaryTypeChange('default')} />
            <span>Default (All Global Next of Kin)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="radio" name="beneficiaryType" value="selected_globals" checked={formData.beneficiaryType === 'selected_globals'} onChange={() => handleBeneficiaryTypeChange('selected_globals')} />
            <span>Select from Global Next of Kin</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="radio" name="beneficiaryType" value="external_only" checked={formData.beneficiaryType === 'external_only'} onChange={() => handleBeneficiaryTypeChange('external_only')} />
            <span>External Beneficiary Only</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input type="radio" name="beneficiaryType" value="mixed" checked={formData.beneficiaryType === 'mixed'} onChange={() => handleBeneficiaryTypeChange('mixed')} />
            <span>Mixed (Global + External)</span>
          </label>
        </div>

        {(formData.beneficiaryType === 'selected_globals' || formData.beneficiaryType === 'mixed') && (
          <div style={{ marginTop: '20px', padding: '15px', background: 'white', borderRadius: '5px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#2E7D32', marginBottom: '10px' }}>Select Global Next of Kin</h4>
            {loadingNoks ? <p>Loading...</p> : globalNoks.length === 0 ? (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px', padding: '10px' }}>
                <p>No Global Next of Kin found. <Link to="/next-of-kin" style={{ color: '#2E7D32' }}>Add Global NOK</Link></p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {globalNoks.map(nok => (
                  <label key={nok._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '5px', background: '#F5F0E6', borderRadius: '3px' }}>
                    <input type="checkbox" checked={formData.selectedGlobalNoks.includes(nok._id)} onChange={() => handleGlobalNokToggle(nok._id)} />
                    <span>{nok.name} ({nok.email})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {(formData.beneficiaryType === 'external_only' || formData.beneficiaryType === 'mixed') && (
          <div style={{ marginTop: '20px', padding: '15px', background: 'white', borderRadius: '5px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#2E7D32', margin: 0 }}>Select External Beneficiaries</h4>
              {userPlan === 'free' && <span style={{ padding: '2px 8px', background: '#FFD700', borderRadius: '3px', fontSize: '11px', fontWeight: 'bold' }}>✨ Premium Feature</span>}
              <button type="button" onClick={() => setShowNewExternalForm(!showNewExternalForm)} style={{ padding: '5px 10px', background: '#2E7D32', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                {showNewExternalForm ? 'Cancel' : '+ New Beneficiary'}
              </button>
            </div>

            {showNewExternalForm && (
              <div style={{ marginBottom: '20px', padding: '15px', background: '#F5F0E6', borderRadius: '5px' }}>
                <h5 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>Create New External Beneficiary</h5>
                <div style={{ marginBottom: '10px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold' }}>Full Name *</label><input type="text" name="newExternal.name" value={formData.newExternalBeneficiary.name} onChange={handleChange} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                <div style={{ marginBottom: '10px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold' }}>Email *</label><input type="email" name="newExternal.email" value={formData.newExternalBeneficiary.email} onChange={handleChange} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                <div style={{ marginBottom: '10px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold' }}>Phone</label><input type="tel" name="newExternal.phone" value={formData.newExternalBeneficiary.phone} onChange={handleChange} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                <div style={{ marginBottom: '10px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold' }}>Relationship</label><select name="newExternal.relationship" value={formData.newExternalBeneficiary.relationship} onChange={handleChange} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}><option value="spouse">Spouse</option><option value="child">Child</option><option value="parent">Parent</option><option value="sibling">Sibling</option><option value="friend">Friend</option><option value="lawyer">Lawyer</option><option value="advisor">Advisor</option><option value="other">Other</option></select></div>
                <button type="button" onClick={handleCreateExternal} style={{ width: '100%', padding: '8px', background: '#FFD700', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}>Create & Select</button>
              </div>
            )}

            {externalBeneficiaries.length === 0 ? (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px', padding: '10px' }}><p>No external beneficiaries yet. Create one above!</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {externalBeneficiaries.map(ben => (
                  <label key={ben.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '5px', background: '#F5F0E6', borderRadius: '3px' }}>
                    <input type="checkbox" checked={formData.selectedExternalBeneficiaries.includes(ben.id)} onChange={() => handleExternalToggle(ben.id)} disabled={userPlan === 'free'} />
                    <span style={{ opacity: userPlan === 'free' ? 0.6 : 1 }}>{ben.name} ({ben.email}){ben.phone && ` • ${ben.phone}`}</span>
                    {userPlan === 'free' && <span style={{ marginLeft: '5px', fontSize: '12px' }}>🔒</span>}
                  </label>
                ))}
              </div>
            )}

            {userPlan === 'free' && formData.selectedExternalBeneficiaries.length > 0 && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px', padding: '15px', marginTop: '15px', textAlign: 'center' }}>
                <p style={{ fontWeight: 'bold' }}>✨ Unlock External Beneficiaries</p>
                <p>You've selected {formData.selectedExternalBeneficiaries.length} external beneficiary(s). Upgrade to Premium to use them.</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                  <Link to="/upgrade" style={{ padding: '8px 16px', background: '#FFD700', color: '#1A1A1A', textDecoration: 'none', borderRadius: '5px', fontWeight: 'bold', display: 'inline-block' }}>Upgrade Now → GHS 399 one-time</Link>
                  <button type="button" onClick={() => setFormData({ ...formData, selectedExternalBeneficiaries: [] })} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #666', borderRadius: '5px', cursor: 'pointer' }}>Remove Selection</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    const getFormFields = () => {
      const commonFields = (
	    <div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    Asset Name <span style={{ color: '#dc2626' }}>*</span>
  </label>
  <input 
    type="text" 
    name="assetName" 
    value={formData.assetName} 
    onChange={handleChange} 
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
    placeholder="e.g., GCB Main Account, Family Home, Toyota Camry" 
    required 
  />
  <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
    A short name to identify this asset (required)
  </small>
</div> 
           );

      switch(assetType) {
        case 'bank_account':
          return (
            <>
              {commonFields}
              <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>🏦 Bank Account Details</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Institution Name *</label><input type="text" name="institutionName" value={formData.institutionName} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="e.g., GCB, Stanbic, Ecobank" required /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Account Number *</label><input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="Enter account number" required /></div>
<div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
  <div style={{ flex: 1, minWidth: '150px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Currency</label>
    <select name="currency" value={formData.currency} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
      <option value="GHS">₵ GHS</option>
      <option value="USD">$ USD</option>
      <option value="GBP">£ GBP</option>
      <option value="EUR">€ EUR</option>
      <option value="NGN">₦ NGN</option>
      <option value="ZAR">R ZAR</option>
      <option value="CAD">CA$ CAD</option>
      <option value="AUD">A$ AUD</option>
	  <option value="Other">Other (please specify)</option>
    </select>
    {formData.currency === 'Other' && (
      <input
        type="text"
        name="currencyOther"
        value={formData.currencyOther || ''}
        onChange={handleChange}
        placeholder="Enter currency code (e.g., JPY, CHF, INR)"
        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '8px' }}
      />
    )}
  </div>
  <div style={{ flex: 2, minWidth: '200px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Current Balance *</label>
    <input type="number" name="balance" value={formData.balance} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="0.00" step="0.01" />
  </div>
</div>
            </>
          );
        case 'real_estate':
          return (
            <>
              {commonFields}
              <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>🏠 Real Estate Details</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Property Type *</label><select name="propertyType" value={formData.propertyType} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} required><option value="">Select</option><option value="residential">Residential Home</option><option value="commercial">Commercial</option><option value="land">Land</option><option value="apartment">Apartment/Condo</option></select></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Address *</label><textarea name="address" value={formData.address} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '80px' }} placeholder="Full property address" required /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Title Deed Number *</label><input type="text" name="titleDeedNumber" value={formData.titleDeedNumber} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="Deed registration number" required /></div>
<div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
  <div style={{ flex: 1, minWidth: '150px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Currency</label>
    <select name="currency" value={formData.currency} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
      <option value="GHS">₵ GHS</option>
      <option value="USD">$ USD</option>
      <option value="GBP">£ GBP</option>
      <option value="EUR">€ EUR</option>
      <option value="NGN">₦ NGN</option>
      <option value="ZAR">R ZAR</option>
      <option value="CAD">CA$ CAD</option>
      <option value="AUD">A$ AUD</option>
	  <option value="Other">Other (please specify)</option>
    </select>
    {formData.currency === 'Other' && (
      <input
        type="text"
        name="currencyOther"
        value={formData.currencyOther || ''}
        onChange={handleChange}
        placeholder="Enter currency code (e.g., JPY, CHF, INR)"
        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '8px' }}
      />
    )}
  </div>
  <div style={{ flex: 2, minWidth: '200px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Estimated Value *</label>
    <input 
      type="number" 
      name="estimatedValue" 
      value={formData.estimatedValue} 
      onChange={handleChange} 
      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
      placeholder="0.00" 
      step="0.01"
    />
  </div>
</div>
            </>
          );
        case 'vehicle':
          return (
            <>
              {commonFields}
              <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>🚗 Vehicle Details</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Make *</label><input type="text" name="make" value={formData.make} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="e.g., Toyota, Mercedes" required /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Model *</label><input type="text" name="model" value={formData.model} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="e.g., Camry, C-Class" required /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Year *</label><input type="number" name="year" value={formData.year} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="2020" min="1900" max={new Date().getFullYear()} required /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>VIN *</label><input type="text" name="vin" value={formData.vin} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="Vehicle Identification Number" required /></div>
<div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
  <div style={{ flex: 1, minWidth: '150px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Currency</label>
    <select name="currency" value={formData.currency} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
      <option value="GHS">₵ GHS</option>
      <option value="USD">$ USD</option>
      <option value="GBP">£ GBP</option>
      <option value="EUR">€ EUR</option>
      <option value="NGN">₦ NGN</option>
      <option value="ZAR">R ZAR</option>
      <option value="CAD">CA$ CAD</option>
      <option value="AUD">A$ AUD</option>
	  <option value="Other">Other (please specify)</option>
    </select>
    {formData.currency === 'Other' && (
      <input
        type="text"
        name="currencyOther"
        value={formData.currencyOther || ''}
        onChange={handleChange}
        placeholder="Enter currency code (e.g., JPY, CHF, INR)"
        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '8px' }}
      />
    )}
  </div>
  <div style={{ flex: 2, minWidth: '200px' }}>
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Estimated Value *</label>
    <input 
      type="number" 
      name="estimatedValue" 
      value={formData.estimatedValue} 
      onChange={handleChange} 
      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
      placeholder="0.00" 
      step="0.01"
    />
  </div>
</div>
            </>
          );
        case 'will':
          return (
            <>
              {commonFields}
              <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>📜 Last Will & Testament</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Upload Will Document *</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} />{fileName && <small style={{ fontSize: '12px', color: '#2E7D32', marginTop: '5px', display: 'block' }}>Selected: {fileName}</small>}<small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>Upload your signed will document</small></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Date Signed</label><input type="date" name="dateSigned" value={formData.dateSigned} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Witnesses</label><textarea name="witnesses" value={formData.witnesses} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '60px' }} placeholder="Names of witnesses (one per line)" /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Executor Name</label><input type="text" name="executorName" value={formData.executorName} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} placeholder="Name of executor" /></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Special Instructions</label><textarea name="specialInstructions" value={formData.specialInstructions} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '80px' }} placeholder="Any special instructions for your family" /></div>
            </>
          );
		  case 'secure_note':
  return (
    <>
      {commonFields}
      <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>🔒 Secure Note</h3>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Note Title <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="secureNoteTitle" 
          value={formData.secureNoteTitle} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="e.g., Family Safe Combination, WiFi Password, Private Instructions" 
          required 
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          What do you want to store? <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <textarea 
          name="secureNoteContent" 
          value={formData.secureNoteContent} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '200px' }} 
          placeholder="Enter your private information here. This will be encrypted and only accessible by your beneficiaries after death verification."
          required 
        />
        <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
          This content will be encrypted. Only you and your designated beneficiaries can read it.
        </small>
      </div>
    </>
  );
  
  case 'license_code':
  return (
    <>
      {commonFields}
      <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>📜 License/Code</h3>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Product Name <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="productName" 
          value={formData.productName} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="e.g., Adobe Creative Suite, Windows 11 Pro" 
          required 
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          License Key/Code <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="licenseKey" 
          value={formData.licenseKey} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="XXXXX-XXXXX-XXXXX-XXXXX" 
          required 
        />
        <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
          This license key will be encrypted and only accessible by your beneficiaries after death verification.
        </small>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Expiry Date (Optional)
        </label>
        <input 
          type="date" 
          name="expiryDate" 
          value={formData.expiryDate} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Vendor (Optional)
        </label>
        <input 
          type="text" 
          name="vendor" 
          value={formData.vendor} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="e.g., Microsoft, Adobe, Apple" 
        />
      </div>
    </>
  );
  
  case 'custom_document':
  return (
    <>
      {commonFields}
      <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>📋 Custom Document</h3>
      
      {/* Document Title */}
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Document Title <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="assetName" 
          value={formData.assetName} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="e.g., Family Recipe, Project Blueprint, Software License" 
          required 
        />
      </div>
      
      {/* Dynamic Custom Fields */}
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
          Custom Fields
        </label>
        
        {/* Existing fields list */}
        {formData.customFields.length > 0 && (
          <div style={{ marginBottom: '15px', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
            {formData.customFields.map((field, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '10px', padding: '10px', borderBottom: '1px solid #eee', background: '#F9F9F9', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Field name"
                  value={field.fieldName}
                  onChange={(e) => updateCustomField(idx, 'fieldName', e.target.value)}
                  style={{ flex: 2, minWidth: '120px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <input
                  type="text"
                  placeholder="Field value"
                  value={field.fieldValue}
                  onChange={(e) => updateCustomField(idx, 'fieldValue', e.target.value)}
                  style={{ flex: 3, minWidth: '150px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <button
                  type="button"
                  onClick={() => removeCustomField(idx)}
                  style={{ padding: '8px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Add new field form */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Field Name</label>
            <input
              type="text"
              placeholder="e.g., Software Name"
              value={formData.newFieldName}
              onChange={(e) => setFormData({ ...formData, newFieldName: e.target.value })}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div style={{ flex: 3, minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Field Value</label>
            <input
              type="text"
              placeholder="e.g., Adobe Photoshop"
              value={formData.newFieldValue}
              onChange={(e) => setFormData({ ...formData, newFieldValue: e.target.value })}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <button
            type="button"
            onClick={addCustomField}
            style={{ padding: '10px 20px', background: '#2E7D32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}
          >
            + Add Field
          </button>
        </div>
        <small style={{ fontSize: '12px', color: '#666', marginTop: '8px', display: 'block' }}>
          Add as many custom fields as you need to describe this document.
        </small>
      </div>
    </>
  );
  
  case 'login_credential':
  return (
    <>
      {commonFields}
      <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>🔐 Login Credential</h3>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Platform Name <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="platformName" 
          value={formData.platformName} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="e.g., Gmail, Facebook, Mobile Banking App" 
          required 
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Username/Email <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="text" 
          name="username" 
          value={formData.username} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="Username or email address" 
          required 
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Password <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input 
          type="password" 
          name="password" 
          value={formData.password} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="Password" 
          required 
        />
        <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
          This password will be encrypted and only accessible by your beneficiaries after death verification.
        </small>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
          Login URL (Optional)
        </label>
        <input 
          type="url" 
          name="loginUrl" 
          value={formData.loginUrl} 
          onChange={handleChange} 
          style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
          placeholder="https://example.com/login" 
        />
      </div>
    </>
  );
  
        default:
          return (
            <>
              {commonFields}
              <h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>Asset Details</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Description *</label><textarea name="description" value={formData.description} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '100px' }} placeholder="Describe your asset" required /></div>
              {assetType === 'document' && (
                <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Upload Document</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} />{fileName && <small style={{ fontSize: '12px', color: '#2E7D32', marginTop: '5px', display: 'block' }}>Selected: {fileName}</small>}<small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>Max file size: 5MB</small></div>
              )}
            </>
          );
      }
    };

    return (
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
          <button type="button" onClick={() => setStep(1)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #1A1A1A', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' }}>← Change Type</button>
          <h2 style={{ fontSize: '20px', color: '#FFD700', margin: 0 }}>Add {assetTypes.find(t => t.value === assetType)?.label}</h2>
        </div>

        {getFormFields()}

{/* CATEGORY DROPDOWN - appears for all asset types */}
<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    Category
  </label>
  <select 
    name="category" 
    value={formData.category} 
    onChange={handleChange}
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}
  >
    <option value="personal">👤 Personal</option>
    <option value="financial">💰 Financial</option>
    <option value="property">🏠 Property</option>
    <option value="vehicle">🚗 Vehicle</option>
    <option value="work">💼 Work</option>
    <option value="family">👨‍👩‍👧‍👦 Family</option>
    <option value="legal">⚖️ Legal</option>
    <option value="other">📦 Other</option>
  </select>
  <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
    Helps organize your assets for easier searching
  </small>
</div>

{/* LOCATION FIELDS - appears for almost all asset types */}
<h3 style={{ fontSize: '18px', color: '#2E7D32', marginTop: '20px', marginBottom: '15px' }}>📍 Where is Your Asset Located? (Optional)</h3>

<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    Full Address
  </label>
  <input 
    type="text" 
    name="location.fullAddress" 
    value={formData.location?.fullAddress || ''} 
    onChange={handleChange} 
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
    placeholder="Street address, city, building name, or landmark" 
  />
</div>

<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    State / Province / Region
  </label>
  <input 
    type="text" 
    name="location.state" 
    value={formData.location?.state || ''} 
    onChange={handleChange} 
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
    placeholder="e.g., Ashanti, Greater Accra, California, Ontario" 
  />
</div>

<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    Country
  </label>
  <select 
    name="location.country" 
    value={formData.location?.country || ''} 
    onChange={handleChange}
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}
  >
    <option value="">Select country</option>
    <option value="Ghana">Ghana</option>
    <option value="Nigeria">Nigeria</option>
    <option value="South Africa">South Africa</option>
    <option value="Kenya">Kenya</option>
    <option value="United Kingdom">United Kingdom</option>
    <option value="United States">United States</option>
    <option value="Canada">Canada</option>
    <option value="Germany">Germany</option>
    <option value="France">France</option>
    <option value="Australia">Australia</option>
    <option value="Other">Other (please specify)</option>
  </select>
  {formData.location?.country === 'Other' && (
    <input 
      type="text" 
      name="location.countryOther" 
      value={formData.location?.countryOther || ''} 
      onChange={handleChange} 
      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '8px' }} 
      placeholder="Please specify your country" 
    />
  )}
</div>

<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
    GPS Coordinates
  </label>
  <input 
    type="text" 
    name="location.gpsCoordinates" 
    value={formData.location?.gpsCoordinates || ''} 
    onChange={handleChange} 
    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} 
    placeholder="e.g., 5.6037° N, 0.1870° W" 
  />
  <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
    You can get GPS coordinates from Google Maps by right-clicking on a location
  </small>
</div>

        <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Additional info on Asset (Visible to your Next of Kin)</label><textarea name="description" value={formData.description} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '80px' }} placeholder="Describe this asset for your family" /><small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>Your Next of Kin will see this</small></div>
        <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Private Notes (Only You can see)</label><textarea name="notes" value={formData.notes} onChange={handleChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px', minHeight: '80px' }} placeholder="Your private notes about this asset" /><small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>Only you can see these notes</small></div>

        {assetType !== 'will' && assetType !== 'document' && (
          <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Supporting Document (Optional)</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }} />{fileName && <small style={{ fontSize: '12px', color: '#2E7D32', marginTop: '5px', display: 'block' }}>Selected: {fileName}</small>}<small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>Max size: {userPlan === 'free' ? '2MB' : userPlan === 'premium' ? '5MB' : userPlan === 'business' ? '10MB' : '20MB'}</small></div>
        )}

        {uploading && uploadProgress > 0 && uploadProgress < 100 && (
          <div style={{ marginBottom: '15px', background: '#f0f0f0', borderRadius: '5px', overflow: 'hidden' }}>
            <div style={{ height: '4px', background: '#FFD700', transition: 'width 0.3s ease', width: `${uploadProgress}%` }}></div>
            <small style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>Uploading... {uploadProgress}%</small>
          </div>
        )}

        {renderBeneficiarySelection()}

        {error && <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#dc2626', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>{error}</div>}
        {success && <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>{success}</div>}

        <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
          <button type="submit" style={{ flex: '1', padding: '12px', background: '#FFD700', border: 'none', borderRadius: '5px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }} disabled={loading}>{loading ? 'Saving...' : 'Save Asset to Vault'}</button>
          <Link to="/dashboard" style={{ padding: '12px 24px', background: 'transparent', color: '#666', border: '1px solid #666', borderRadius: '5px', textDecoration: 'none', fontSize: '16px', textAlign: 'center' }}>Cancel</Link>
        </div>
      </form>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', flexDirection: 'column' }}>
  <div style={{ padding: '20px', flex: 1 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', maxWidth: '1000px', margin: '0 auto 30px', width: '100%' }}>
      <h1 style={{ fontSize: '28px', color: '#1A1A1A', margin: 0 }}>Add New Asset</h1>
      <Link to="/dashboard" style={{ color: '#2E7D32', textDecoration: 'none', fontSize: '14px' }}>← Back to Dashboard</Link>
    </div>

    <div style={{ background: 'white', borderRadius: '10px', padding: '30px', maxWidth: '1000px', margin: '0 auto 30px', width: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', boxSizing: 'border-box' }}>
      {step === 1 ? renderTypeSelection() : renderForm()}
    </div>
  </div>

  <Footer />


      <style dangerouslySetInnerHTML={{ __html: `
	  /* Add to your existing style tag */

/* Ensure form card has proper padding on all sides */
.addasset-card {
  padding: 30px !important;
  box-sizing: border-box !important;
}

/* Ensure all form groups have proper spacing from edges */
.form-group {
  margin-bottom: 20px;
  width: 100%;
}

/* Ensure inputs don't touch the edges */
input, select, textarea {
  width: 100%;
  box-sizing: border-box;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .addasset-card {
    padding: 20px !important;
  }
  
  .form-group {
    margin-bottom: 16px;
  }
}

@media (max-width: 480px) {
  .addasset-card {
    padding: 15px !important;
  }
  
  .form-group {
    margin-bottom: 14px;
  }
}
        @media (min-width: 1025px) { .asset-grid { grid-template-columns: repeat(4, 1fr) !important; } }
        @media (min-width: 769px) and (max-width: 1024px) { .asset-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 15px !important; } }
        @media (max-width: 768px) {
          .asset-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
          div[style*="display: flex"][style*="justify-content: space-between"] { flex-direction: column !important; text-align: center !important; gap: 10px !important; margin-bottom: 20px !important; }
          div[style*="display: flex"][style*="gap: 20px"] { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; }
          div[style*="display: flex"][style*="gap: 15px"] { flex-direction: column !important; gap: 12px !important; }
          div[style*="padding: 30px"] { padding: 20px !important; }
          h1 { font-size: 24px !important; }
        }
        @media (max-width: 480px) {
          h1 { font-size: 20px !important; }
          div[style*="padding: 30px"] { padding: 15px !important; }
        }
        input[type="radio"], input[type="checkbox"] { margin: 0 !important; padding: 0 !important; width: 18px !important; height: 18px !important; flex-shrink: 0 !important; }
      `}} />
    </div>
  );
};

export default AddAsset;